/**
 * DecisionFlow — CoverageView
 * ui/src/components/admin/CoverageView.jsx
 * S611
 *
 * Compensation coverage panel. Mounted in AssignmentsTab above PopulationAnalysisPanel.
 * Answers: "Is variable comp budget deployed against the right sales motions?"
 *
 * Spec authority: comp_policies_spec_v2_2.md §9
 *
 * Data: client-side join — no new backend endpoint.
 *   Plans:       already fetched in CompensationPoliciesWorkspace (passed via period)
 *   Roles:       GET /api/v1/roles
 *   AOP inputs:  shell-level (allPlanInputs from CompAdminShell)
 *   Assignments: GET /api/v1/plan-assignments
 *
 * Motion grouping: role_category + role_code pattern matching (proxy).
 * Post-GA: sales_motion column on role table replaces pattern matching.
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind colors. Zero dark: variants.
 */

import { useState, useEffect, useMemo } from "react";
import { RefreshCw, Info } from "lucide-react";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Motion grouping — sales_motion_id FK join (D-CRL-02, S614 m_s614_crl_02)
// Replaces role_code pattern matching permanently (OI-COVERAGE-VIEW-ROLE-MOTION closed).
// motionMap: selling_motion_id → { motion_code, motion_name } fetched on mount.
// Roles without sales_motion_id (not yet adopted via CRL) fall to "unclassified".
// ---------------------------------------------------------------------------
function classifyRole(role, motionMap) {
  if (role.sales_motion_id && motionMap[role.sales_motion_id]) {
    return motionMap[role.sales_motion_id].motion_name;
  }
  // Fallback for roles not yet adopted via CRL: use role_category as proxy label
  if (["rollup", "hybrid"].includes(role.role_category)) return "__management__";
  return "__unclassified__";
}

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------
function useFetch(url) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!url) { setLoading(false); return; }
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------
function fmt$(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(n, total) {
  if (!total) return "—";
  return `${Math.round((n / total) * 100)}%`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CoverageView({ period }) {
  const fiscalPeriodId = period?.fiscal_period_id ?? null;
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: rolesData,       loading: rolesLoading }       = useFetch(`${API_BASE}/roles`);
  const { data: motionsData,     loading: motionsLoading }     = useFetch(`${API_BASE}/selling-motions`);
  const { data: assignmentsData, loading: assignmentsLoading } = useFetch(
    fiscalPeriodId
      ? `${API_BASE}/plan-assignments?fiscal_period_id=${fiscalPeriodId}`
      : null
  );
  const { data: plansData,       loading: plansLoading }       = useFetch(
    fiscalPeriodId
      ? `${API_BASE}/plans?fiscal_period_id=${fiscalPeriodId}&plan_status=approved`
      : null
  );
  const { data: aopData,         loading: aopLoading }         = useFetch(
    fiscalPeriodId
      ? `${API_BASE}/financial-alignment?fiscal_period_id=${fiscalPeriodId}`
      : null
  );

  const loading = rolesLoading || motionsLoading || assignmentsLoading || plansLoading || aopLoading;

  // Build selling motion map: selling_motion_id → { motion_code, motion_name }
  const motionMap = useMemo(() => {
    const motions = motionsData?.data ?? motionsData?.selling_motions ?? [];
    const map = {};
    motions.forEach((m) => { map[m.selling_motion_id] = m; });
    return map;
  }, [motionsData]);

  // Build role map
  const roleMap = useMemo(() => {
    const roles = rolesData?.roles ?? rolesData?.data ?? [];
    const map = {};
    roles.forEach((r) => { map[r.role_id] = r; });
    return map;
  }, [rolesData]);

  // Build coverage rows — FK-join grouping via sales_motion_id
  const { rows, totals, aopApproved } = useMemo(() => {
    const plans       = plansData?.plans ?? plansData?.data ?? [];
    const assignments = assignmentsData?.assignments ?? assignmentsData?.data ?? [];

    // AOP data
    const aopApproved = aopData?.status === "approved" || aopData?.finance_plan_status === "approved";
    const aopByRole   = {}; // role_id → planned_variable_comp
    const aopInputs   = aopData?.aop_inputs ?? aopData?.inputs ?? [];
    aopInputs.forEach((inp) => {
      if (inp.role_id) {
        aopByRole[inp.role_id] = (aopByRole[inp.role_id] ?? 0) + (inp.total_planned_variable_comp ?? 0);
      }
    });

    // Buckets keyed by motion_name (from FK join) or sentinel strings for fallbacks
    const buckets = {};
    const _getOrCreate = (key) => {
      if (!buckets[key]) buckets[key] = { plans: new Set(), reps: new Set(), budget: 0 };
      return buckets[key];
    };

    // Assign plans to buckets via role → sales_motion_id FK
    plans.forEach((plan) => {
      const role   = roleMap[plan.role_id];
      const key    = role ? classifyRole(role, motionMap) : "__unclassified__";
      _getOrCreate(key).plans.add(plan.plan_id);
      if (role && aopByRole[role.role_id]) {
        _getOrCreate(key).budget += aopByRole[role.role_id];
      }
    });

    // Count reps via assignments
    assignments.forEach((asgn) => {
      const plan = plans.find((p) => p.plan_id === asgn.plan_id);
      const role = plan ? roleMap[plan.role_id] : null;
      const key  = role ? classifyRole(role, motionMap) : "__unclassified__";
      _getOrCreate(key).reps.add(asgn.position_id ?? asgn.employee_id ?? asgn.assignment_id);
    });

    // Build rows — declared motions first (from motionMap, sorted by motion_name),
    // then management fallback, then unclassified
    const motionKeys = Object.values(motionMap)
      .sort((a, b) => a.motion_name.localeCompare(b.motion_name))
      .map((m) => m.motion_name)
      .filter((k) => buckets[k]);

    const allKeys = [
      ...motionKeys,
      ...(buckets["__management__"] ? ["__management__"] : []),
      ...(buckets["__unclassified__"] ? ["__unclassified__"] : []),
    ];

    const rows = allKeys.map((key) => ({
      key,
      label:  key === "__management__"
                ? "Management"
                : key === "__unclassified__"
                  ? "Unclassified"
                  : key,
      plans:  buckets[key]?.plans.size ?? 0,
      reps:   buckets[key]?.reps.size ?? 0,
      budget: buckets[key]?.budget ?? 0,
    }));

    const totals = {
      plans:  rows.reduce((s, r) => s + r.plans, 0),
      reps:   rows.reduce((s, r) => s + r.reps, 0),
      budget: rows.reduce((s, r) => s + r.budget, 0),
    };

    return { rows, totals, aopApproved };
  }, [plansData, assignmentsData, roleMap, motionMap, aopData]);

  const periodLabel = period?.period_label ?? period?.period_code ?? "this period";

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden mb-4">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
        <div>
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Compensation Coverage — {periodLabel}
          </h3>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Variable comp budget allocation by sales motion.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* AOP not approved warning */}
      {!aopLoading && !aopApproved && totals.budget === 0 && (
        <div className="flex items-center gap-2 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-warning))]/5 px-4 py-2">
          <Info className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
          <p className="text-tok-xs text-[rgb(var(--color-warning))]">
            AOP VC Budget not yet approved for this period. Coverage shown without budget allocation.
          </p>
        </div>
      )}

      {/* No plans empty state */}
      {!loading && totals.plans === 0 && (
        <div className="px-4 py-8 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No active plans</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Deploy plans from the Plan Library to see coverage.
          </p>
        </div>
      )}

      {/* Coverage table */}
      {(loading || totals.plans > 0) && (
        <table className="w-full border-collapse">
          <thead className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
            <tr>
              <th className="px-4 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Sales Motion
              </th>
              <th className="w-16 px-4 py-2 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Plans
              </th>
              <th className="w-16 px-4 py-2 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                Reps
              </th>
              {aopApproved && (
                <>
                  <th className="w-36 px-4 py-2 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                    AOP VC Budget
                  </th>
                  <th className="w-20 px-4 py-2 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                    % of Total
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5].map((i) => (
                <tr key={i} className="border-b border-[rgb(var(--color-border))]">
                  <td className="px-4 py-2.5"><div className="h-3.5 w-32 rounded skeleton" /></td>
                  <td className="px-4 py-2.5 text-right"><div className="h-3.5 w-6 rounded skeleton ml-auto" /></td>
                  <td className="px-4 py-2.5 text-right"><div className="h-3.5 w-6 rounded skeleton ml-auto" /></td>
                  {aopApproved && (
                    <>
                      <td className="px-4 py-2.5 text-right"><div className="h-3.5 w-20 rounded skeleton ml-auto" /></td>
                      <td className="px-4 py-2.5 text-right"><div className="h-3.5 w-8 rounded skeleton ml-auto" /></td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              rows.map((row) => (
                <tr key={row.key} className="border-b border-[rgb(var(--color-border))] last:border-0">
                  <td className="px-4 py-2.5 text-tok-xs text-[rgb(var(--color-text))]">
                    {row.label}
                  </td>
                  <td className="px-4 py-2.5 text-right text-tok-xs text-[rgb(var(--color-text))]">
                    {row.plans || <span className="text-[rgb(var(--color-text-muted))]">0</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-tok-xs text-[rgb(var(--color-text))]">
                    {row.reps || <span className="text-[rgb(var(--color-text-muted))]">0</span>}
                  </td>
                  {aopApproved && (
                    <>
                      <td className="px-4 py-2.5 text-right text-tok-xs text-[rgb(var(--color-text))]">
                        {row.budget > 0 ? fmt$(row.budget) : <span className="text-[rgb(var(--color-text-muted))]">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right text-tok-xs text-[rgb(var(--color-text-muted))]">
                        {row.budget > 0 ? fmtPct(row.budget, totals.budget) : "0%"}
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>

          {/* Totals row */}
          {!loading && totals.plans > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
                <td className="px-4 py-2.5 text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                  Total
                </td>
                <td className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                  {totals.plans}
                </td>
                <td className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                  {totals.reps}
                </td>
                {aopApproved && (
                  <>
                    <td className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                      {fmt$(totals.budget)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                      100%
                    </td>
                  </>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      )}

      {/* Footer note */}
      <div className="flex items-start gap-2 border-t border-[rgb(var(--color-border))] px-4 py-2.5">
        <Info className="mt-0.5 h-3 w-3 shrink-0 text-[rgb(var(--color-text-muted))]" />
        <p className="text-[10px] text-[rgb(var(--color-text-muted))] leading-relaxed">
          Motion groupings are derived from the primary selling motion assigned to each role via the
          Commercial Role Library. Roles not yet adopted via the library appear as Unclassified.
        </p>
      </div>

    </div>
  );
}
